import { useState, useEffect, useMemo } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useRownd } from '@rownd/react';
import { CircularProgress, Box, Snackbar, Alert, Button } from '@mui/material';
import theme from './theme/theme';
import MainLayout from './layouts/MainLayout';
import SimpleMapTest from './pages/SimpleMapTest';
import TsunamiReportPage from './pages/TsunamiReportPage';
import Home from './pages/Home';
import AdminPage from './pages/AdminPage';
import OfflineNotification from './components/OfflineNotification';
import { useServiceWorker } from './hooks/useServiceWorker';
import { initSyncListeners } from './utils/syncService';

function App() {
  const [currentPage, setCurrentPage] = useState('map');
  const { is_authenticated, is_initializing, user } = useRownd();
  const { isUpdateAvailable = false, updateServiceWorker = () => {}, error: swError } = useServiceWorker() || {};
  const [showUpdateAlert, setShowUpdateAlert] = useState(false);
  const [showAdminAccessDenied, setShowAdminAccessDenied] = useState(false);
  const [previousAuthState, setPreviousAuthState] = useState(false);

  // Check if user has admin access
  const hasAdminAccess = useMemo(() => {
    if (is_authenticated && user) {
      // Try different methods of accessing the email field
      const emailFromData = user.data?.email;
      const emailDirectly = user.email;
      
      // Use the email field based on what's available
      const userEmail = emailFromData || emailDirectly;
      
      if (userEmail) {
        const email = userEmail.toLowerCase();
        const adminDomains = ['.mil', '.gov', '.teambespin.us'];
        
        // Check for exact domain matches and the special case for @teambespin.us
        const hasPermission = adminDomains.some(domain => {
          if (domain === '.teambespin.us' && email.includes('@teambespin.us')) {
            return true;
          }
          return email.endsWith(domain);
        });
        
        return hasPermission;
      }
    }
    return false;
  }, [is_authenticated, user]);

  // Detect authentication state changes and force re-render when needed
  useEffect(() => {
    // Check if auth state changed from false to true (user just logged in)
    if (!previousAuthState && is_authenticated) {
      console.log('User authenticated, refreshing state...');
      // Force current page to refresh by briefly changing and then restoring
      const currentPageCopy = currentPage;
      setCurrentPage('temp-refresh');
      
      // Small delay to ensure state changes are processed
      setTimeout(() => {
        setCurrentPage(currentPageCopy);
      }, 50);
    }
    
    // Update previous auth state
    setPreviousAuthState(is_authenticated);
  }, [is_authenticated]);

  // Initialize the sync listeners for offline/online connectivity
  useEffect(() => {
    console.log('Initializing app and checking service worker status');
    // Call initSyncListeners and store the cleanup function
    const cleanup = initSyncListeners();
    
    // Return the cleanup function for useEffect to call on unmount
    return () => {
      console.log('Cleaning up app resources');
      if (typeof cleanup === 'function') {
        cleanup();
      }
    };
  }, []);

  // Show update notification when a service worker update is available
  useEffect(() => {
    if (isUpdateAvailable) {
      setShowUpdateAlert(true);
    }
  }, [isUpdateAvailable]);

  // Redirect from admin page if user doesn't have access
  useEffect(() => {
    if (currentPage === 'admin' && (!is_authenticated || !hasAdminAccess) && !is_initializing) {
      // If user is trying to access admin page but isn't authenticated with proper email domain, redirect to map
      setCurrentPage('map');
      // Show access denied message
      setShowAdminAccessDenied(true);
    }
  }, [currentPage, is_authenticated, hasAdminAccess, is_initializing]);

  const handleNavigation = (page) => {
    if (page === 'admin' && (!is_authenticated || !hasAdminAccess)) {
      // If user tries to navigate to admin without being authenticated with proper email, show access denied
      console.log('Access denied: Admin page requires authentication with .mil, .gov, or .teambespin.us email domain');
      setShowAdminAccessDenied(true);
      return;
    }
    setCurrentPage(page);
  };

  const handleUpdateServiceWorker = () => {
    updateServiceWorker();
    setShowUpdateAlert(false);
  };

  const handleCloseUpdateAlert = () => {
    setShowUpdateAlert(false);
  };

  const handleCloseAdminAlert = () => {
    setShowAdminAccessDenied(false);
  };

  // Show loading state while Rownd is initializing
  if (is_initializing) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          minHeight="100vh"
        >
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  // Render the appropriate component based on currentPage
  const renderPage = () => {
    switch (currentPage) {
      case 'map':
        return <SimpleMapTest />;
      case 'report':
        return <TsunamiReportPage />;
      case 'admin':
        // Extra safeguard - only render admin page if user is authenticated with approved email domain
        return is_authenticated && hasAdminAccess ? <AdminPage /> : <SimpleMapTest />;
      case 'about':
        return <Home />;
      default:
        return <SimpleMapTest />;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <MainLayout onNavigate={handleNavigation}>
        {renderPage()}
      </MainLayout>
      
      {/* Offline notification component */}
      <OfflineNotification />
      
      {/* Service worker update notification */}
      <Snackbar
        open={showUpdateAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={handleUpdateServiceWorker}>
              Update
            </Button>
          }
          onClose={handleCloseUpdateAlert}
        >
          A new version is available!
        </Alert>
      </Snackbar>
      
      {/* Admin access denied notification */}
      <Snackbar
        open={showAdminAccessDenied}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={handleCloseAdminAlert}
      >
        <Alert 
          severity="warning"
          onClose={handleCloseAdminAlert}
        >
          Admin access is restricted to authenticated users with .mil, .gov, or .teambespin.us email domains.
        </Alert>
      </Snackbar>
      
      {/* Service worker error notification */}
      {swError && (
        <Snackbar
          open={!!swError}
          autoHideDuration={6000}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert severity="warning">
            PWA features may be limited: {swError}
          </Alert>
        </Snackbar>
      )}
    </ThemeProvider>
  );
}

export default App;
